import { getParty } from 'graphql/parties/queries';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import querys from 'services/querys';
import { NewOrderPartyType } from 'types/order';
import useAlert from 'utils/alert';
import { capitalize } from 'utils/helpers';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GQLDeleteAssociation, mutationUpdatePartyOrderData } from '../services/mutations';

const usePartyInfo = (
  partiesList: NewOrderPartyType[],
  handleFilterParties: (parties: NewOrderPartyType[]) => void
) => {
  const { t } = useTranslation();
  const queryCache = useQueryClient();

  const { partyId, id: orderId } = useParams<{
    id: string;
    partyId: string;
  }>();
  const showAlert = useAlert();

  const partySelected = partiesList.find((party) => party._id === partyId);

  const [open, setOpen] = useState(false);
  const [kindsToChange, setKindsToChange] = useState('');

  const getPartyResponse = useQuery(
    ['order-party', partyId],
    () => getParty({ id: partyId || '' }),
    {
      enabled: !!partyId,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  const { mutate: updatePartyOrderData, ...responseUpdatePartyOrderData } = useMutation(
    mutationUpdatePartyOrderData,
    {
      onSuccess: (data) => {
        const { parties } = data.updateOrderSpecifics;
        handleFilterParties(parties);
      },
    }
  );

  const handleUpdatePartyOrderData = (data) => {
    const isBuyer = partySelected?.kinds.includes('Buyer');
    updatePartyOrderData({
      id: orderId,
      partyId,
      ...(isBuyer ? { dataBuyer: data } : { dataSeller: data }),
    });
  };

  const handleToggleModal = () => {
    setOpen((isOpen) => !isOpen);
  };

  const { mutate: deleteAssociation, ...responseDeleteAssociation } = useMutation(
    GQLDeleteAssociation,
    {
      onSuccess: (data) => {
        queryCache.invalidateQueries(['party-order', orderId]);
        showAlert(capitalize(t('dialogs:order-updated')), 'success');
        const { parties } = data.deleteOrderAssociation;
        handleFilterParties(parties);

        responseDeleteAssociation.reset();
      },
    }
  );

  const { mutate: createAssociation, ...responseCreateAssociation } = useMutation(
    querys.createAssociation,
    {
      onSuccess: () => {
        queryCache.invalidateQueries(['party-order', orderId]);
        setOpen(false);
        deleteAssociation({
          id: orderId!,
          partyId,
          kind: kindsToChange,
          linkedToId: '',
        });
        responseCreateAssociation.reset();
      },
    }
  );

  const handleChangeKind = ({
    kindToChange,
    newKind,
  }: {
    kindToChange: string;
    newKind: string;
  }) => {
    setKindsToChange(kindToChange);
    createAssociation({
      id: orderId!,
      partyId,
      kind: newKind,
      linkedToId: '',
      linkedToKind: '',
    });
  };

  return {
    partyId,
    isLoading: getPartyResponse.isLoading,
    isUpdating: responseUpdatePartyOrderData.isLoading,
    isChangingKinds: responseCreateAssociation.isLoading || responseDeleteAssociation.isLoading,
    handleUpdatePartyOrderData,
    partySelected,
    open,
    handleToggleModal,
    handleChangeKind,
    getPartyResponse,
  };
};

export default usePartyInfo;
